import React, { useEffect, useState } from 'react';
import Step3Overview from '../overview/step-3-overview';
import Subtitle from './subtitle';
import Payment from '../payment/payment';
import AddressSummary from './address/address-summary';
import { useAppSelector } from '../../data/store';
import { defineIfSummaryVisible } from '../../domain/shopping-cart-address-provider';
import { ADDRESS_NOT_VALID_EVENT } from '../../domain/shopping-cart-events-provider';
import { sendGaCheckoutEvent } from '../../../../../domain/marketing/gtm-provider';
import { CartSteps } from '../../enums/cart-steps';
import AddressForm from './address/address-form';
import ShippingOptions from './address/shipping-options';
import { getABTest, sendABTestAnalytics } from '../../../../../domain/abtest/abtest-provider';

/**
 * Shopping Cart Step #3.
 */
const CartStep3 = () => {

    const countries = useAppSelector(store => store.root.countries);
    const cartData = useAppSelector(store => store.root.cartData);

    const billToControl = useAppSelector(store => store.billingAddress.billToControl);
    const billingAddressLine1Control = useAppSelector(store => store.billingAddress.addressLine1Control);
    const billingCityControl = useAppSelector(store => store.billingAddress.cityControl);
    const billingCountryIdControl = useAppSelector(store => store.billingAddress.countryIdControl);
    const billingStateIdControl = useAppSelector(store => store.billingAddress.stateIdControl);
    const billingPhoneControl = useAppSelector(store => store.billingAddress.phoneControl);
    const billingPhoneCountryIdControl = useAppSelector(store => store.billingAddress.phoneCountryIdControl);

    const taxIdControl = useAppSelector(store => store.billingAddress.taxIdControl);
    const taxIdSelectedStatusControl = useAppSelector(store => store.billingAddress.taxIdSelectedStatusControl);

    const isShippingDifferentFromBilling = useAppSelector(store => store.shippingAddress.isShippingDifferentFromBilling);
    const shipToControl = useAppSelector(store => store.shippingAddress.shipToControl);
    const addressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);
    const cityControl = useAppSelector(store => store.shippingAddress.cityControl);
    const countryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);
    const stateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);
    const phoneControl = useAppSelector(store => store.shippingAddress.phoneControl);
    const phoneCountryIdControl = useAppSelector(store => store.shippingAddress.phoneCountryIdControl);

    const [isSummaryVisible] = useState(defineIfSummaryVisible(
        countries,
        {
            billToControl,
            addressLine1Control: billingAddressLine1Control,
            cityControl: billingCityControl,
            countryIdControl: billingCountryIdControl,
            stateIdControl: billingStateIdControl,
            phoneControl: billingPhoneControl,
            phoneCountryIdControl: billingPhoneCountryIdControl,

            taxIdControl,
            taxIdSelectedStatusControl,
        },
        cartData,
        isShippingDifferentFromBilling,
        {
            shipToControl,
            addressLine1Control,
            cityControl,
            countryIdControl,
            stateIdControl,
            phoneControl,
            phoneCountryIdControl,
        }
    ));
    const [editAddressSelected, setEditAddressSelected] = useState(false);
    const [isAddressLocatedAtTop] = useState(!isSummaryVisible);

    useEffect(() => {
            sendGaCheckoutEvent(CartSteps.Third, cartData);
        },
        // https://wavesaudio.atlassian.net/browse/DSWEB-3345
        // eslint-disable-next-line
        []
    );

    useEffect(() => {

        // https://wavesaudio.atlassian.net/browse/DSWEB-3281
        const abtest = getABTest('MaxMind');

        if(abtest && abtest.isActive && cartData.maxMindAddress) {
            sendABTestAnalytics(abtest);
        }

    }, [cartData]);

    /**
     * confirm order validation ---> if error, open 'edit address' form.
     */
    useEffect(() => {

        const addressNotValidEventHandler = () => {
            setEditAddressSelected(true);

            // scroll to the 'edit address' form
            const $el = document.getElementById('billTo');
            if(!$el) return;

            $el.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
        };

        document.addEventListener(ADDRESS_NOT_VALID_EVENT, addressNotValidEventHandler);

        return () => {
            document.removeEventListener(ADDRESS_NOT_VALID_EVENT, addressNotValidEventHandler);
        };
    }, []);

    return (
        <div className="flex flex-wrap w-full pb-10">

            <div className="w-full md:w-7/12 lg:w-3/4 mb-6 md:mb-0">
                <div className="bg-white border rounded md:mr-3 lg:mr-6 px-5 md:px-10">
                    <div className="my-6 max-w-full" style={{
                        width: '430px'
                    }}>
                        {
                            isAddressLocatedAtTop &&
                            <>
                                <Subtitle text="Billing Address" number="1" qa={ 'cart-step3-bill-title' } />

                                {
                                    isSummaryVisible && !editAddressSelected ?
                                        <AddressSummary setEditAddressSelected={ setEditAddressSelected } /> : <AddressForm />
                                }

                                { cartData.isShippingNeeded && <ShippingOptions /> }

                                <Subtitle text="Payment Method" number="2" />
                                <Payment />
                            </>
                        }

                        {
                            !isAddressLocatedAtTop &&
                            <>
                                <Subtitle text="Payment Method" number="1" />
                                <Payment />

                                <Subtitle text="Billing Address" number="2" qa={ 'cart-step3-bill-title' } />

                                {
                                    isSummaryVisible && !editAddressSelected ?
                                        <AddressSummary setEditAddressSelected={setEditAddressSelected} /> : <AddressForm />
                                }

                                { cartData.isShippingNeeded && <ShippingOptions /> }
                            </>
                        }
                    </div>
                </div>
            </div>

            <Step3Overview />
        </div>
    )
};

export default CartStep3;
